import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search as SearchIcon, X } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { PRODUCTS, CATEGORIES } from "../mock";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const SORTS = [
  { v: "popularity", label: "Popularity" },
  { v: "stock", label: "In stock first" },
  { v: "price-asc", label: "Price: Low to High" },
  { v: "price-desc", label: "Price: High to Low" },
  { v: "name-asc", label: "Name: A to Z" },
  { v: "name-desc", label: "Name: Z to A" },
];

const stockRank = { "in-stock": 0, low: 1, "pre-order": 2, "out-of-stock": 3 };

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [cat, setCat] = useState(params.get("cat") || "all");
  const [sort, setSort] = useState("popularity");
  const [q, setQ] = useState(params.get("q") || "");

  useEffect(() => {
    setCat(params.get("cat") || "all");
    setQ(params.get("q") || "");
  }, [params]);

  const selectCat = (slug) => {
    setCat(slug);
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    setParams(next);
  };

  const filtered = useMemo(() => {
    let list = [...PRODUCTS];
    if (cat !== "all") list = list.filter((p) => p.category === cat);
    if (q.trim()) {
      const t = q.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(t) || p.short.toLowerCase().includes(t)
      );
    }
    switch (sort) {
      case "price-asc": list.sort((a, b) => a.price - b.price); break;
      case "price-desc": list.sort((a, b) => b.price - a.price); break;
      case "name-asc": list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "name-desc": list.sort((a, b) => b.name.localeCompare(a.name)); break;
      case "stock": list.sort((a, b) => stockRank[a.stock] - stockRank[b.stock]); break;
      default: list.sort((a, b) => b.popularity - a.popularity);
    }
    return list;
  }, [cat, sort, q]);

  const available = PRODUCTS.filter((p) => p.stock !== "out-of-stock").length;

  return (
    <div>
      {/* header band */}
      <div className="bg-gradient-to-b from-blue-50 to-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Live Catalogue</span>
          <h1 className="mt-1 font-display font-extrabold text-4xl text-slate-900">Research Peptides</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            {PRODUCTS.length} compounds available — {available} available to order. All batches third-party
            verified with HPLC analysis before dispatch.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <SearchIcon className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search compounds…"
              className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 lg:ml-auto">
            <SlidersHorizontal className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-500 hidden sm:inline">Sort by</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[190px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* category chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              onClick={() => selectCat(c.slug)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                cat === c.slug
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* results */}
        <div className="mb-4 text-sm text-slate-500">{filtered.length} result{filtered.length !== 1 && "s"}</div>
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 py-20 text-center">
            <p className="text-slate-500">No compounds match your search.</p>
            <button onClick={() => { setQ(""); selectCat("all"); }} className="mt-3 text-blue-600 font-semibold hover:underline">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        <p className="mt-10 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 leading-relaxed">
          <span className="font-semibold text-slate-700">Research Use Only:</span> All peptides listed are intended
          strictly for in-vitro laboratory research. Not for human or veterinary consumption. You must be a qualified
          researcher aged 18+ to purchase.
        </p>
      </div>
    </div>
  );
};

export default Shop;
